import { useQuery } from '@tanstack/react-query';
import { TreeSelect, Checkbox, Flex } from 'antd';
import { useEffect, useState } from 'react';
import { getObjGroupsDropdown } from '@martech-solution/utils/apis';
import { TreeItem } from '@martech-solution/utils/types';
import { buildHierarchySelect } from 'src/utils/collection';
import { filterExactMatchWithParents } from '@martech-solution/utils/utils';

interface Props {
  objectTypeId?: number;
  value?: React.SetStateAction<number | undefined>;
  style?: React.CSSProperties | undefined;
  allowClear?: boolean | undefined;
  onChange?: (value: React.SetStateAction<number | undefined>, label: React.ReactNode[]) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  popupMatchSelectWidth?: boolean | number | undefined;
  showExactMatch?: boolean;
}

export default function ObjectGroupTreeSelect({
  objectTypeId,
  value,
  style,
  allowClear = true,
  onChange,
  disabled,
  className,
  placeholder = 'Chọn nhóm đối tượng',
  popupMatchSelectWidth,
  showExactMatch = true
}: Props) {
  const [objectGroupTreeItem, setObjectGroupTreeItem] = useState<TreeItem[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [exactMatch, setExactMatch] = useState(false);
  const [filteredTreeData, setFilteredTreeData] = useState<TreeItem[]>();

  const objectGroupDropdownQuery = useQuery({
    queryKey: ['objectGroupsTreeDropdown', objectTypeId],
    queryFn: () => getObjGroupsDropdown(objectTypeId),
    staleTime: 5 * 60 * 1000
  });

  useEffect(() => {
    const result = buildHierarchySelect(objectGroupDropdownQuery.data?.data) || [];
    setObjectGroupTreeItem(result);
    setFilteredTreeData(result);
  }, [objectGroupDropdownQuery.data?.data]);

  useEffect(() => {
    if (!objectGroupTreeItem) return;

    if (exactMatch && searchValue.trim()) {
      setFilteredTreeData(filterExactMatchWithParents(objectGroupTreeItem, searchValue));
    } else {
      setFilteredTreeData(objectGroupTreeItem);
    }
  }, [exactMatch, searchValue, objectGroupTreeItem]);

  return (
    <Flex align='center' gap={8}>
      <TreeSelect
        popupMatchSelectWidth={popupMatchSelectWidth ?? 400}
        treeLine
        showSearch
        value={objectGroupDropdownQuery.isFetching ? undefined : value}
        disabled={disabled}
        placeholder={placeholder}
        className={className}
        style={{ flex: 1, ...style }}
        dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
        treeNodeFilterProp='value'
        allowClear={allowClear}
        onChange={onChange}
        treeData={filteredTreeData}
        loading={objectGroupDropdownQuery.isFetching}
        onSearch={setSearchValue}
        filterTreeNode={
          exactMatch
            ? false
            : (inputValue, treeNode) =>
                treeNode.title?.toString().toLowerCase().includes(inputValue.toLowerCase()) ?? false
        }
      />
      {showExactMatch && (
        <Checkbox checked={exactMatch} onChange={(e) => setExactMatch(e.target.checked)}>
          Lọc chính xác
        </Checkbox>
      )}
    </Flex>
  );
}
